import { useState, useEffect } from 'react';
import { apiErrorText } from '../lib/apiError';
import { MapPin, Loader2, RefreshCw, CloudSun } from 'lucide-react';
import { toast } from 'sonner';
import ServiceToggle from './ServiceToggle';
import apiV2 from '../utils/apiV2';

const WeatherSettings = () => {
  const orgId = localStorage.getItem('organizationId');
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  useEffect(() => {
    let dead = false;
    apiV2.get(`/organizations/${orgId}/weather/config`)
      .then((r) => { if (!dead) setCfg(r.data); })
      .catch(() => toast.error('Could not load weather settings'))
      .finally(() => { if (!dead) setLoading(false); });
    apiV2.get(`/organizations/${orgId}/weather`)
      .then((r) => { if (!dead && r.data?.weather?.fetchedAt) setFetchedAt(r.data.weather.fetchedAt); })
      .catch(() => {});
    return () => { dead = true; };
  }, [orgId]);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      const r = await apiV2.put(`/organizations/${orgId}/weather/config`, patch);
      setCfg(r.data);
    } catch {
      toast.error('Could not save weather settings');
    }
  };

  const pickLocation = (loc) => {
    save({ location: { name: loc.name, lat: loc.lat, lng: loc.lng }, enabled: true });
    toast.success(`Weather location set to ${loc.name}`);
  };

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const r = await apiV2.post(`/organizations/${orgId}/weather/refresh`);
      setFetchedAt(r.data?.weather?.fetchedAt || null);
      toast.success('Forecast refreshed');
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not refresh forecast'));
    }
    setRefreshing(false);
  };

  if (loading || !cfg) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  const isSelected = (p) => cfg.location && Math.abs(cfg.location.lat - p.lat) < 0.001 && Math.abs(cfg.location.lng - p.lng) < 0.001;

  return (
    <div className="max-w-2xl space-y-6" data-testid="weather-settings">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <CloudSun className="w-5 h-5 text-sky-500" /> Weather
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Marine weather, wind, tide and moon for your operating area — shown in the dashboard
          sidebar for you and your crew, and optionally sent to customers on trip day.
        </p>
      </div>

      {/* Enable */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 p-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">Show weather on the dashboard</div>
          <div className="text-xs text-gray-500 mt-0.5">Visible to admins and team members in the sidebar</div>
        </div>
        <ServiceToggle
          checked={cfg.enabled}
          onChange={(v) => save({ enabled: v })}
          data-testid="weather-enabled-toggle"
        />
      </div>

      {/* Location */}
      <div className="rounded-xl border border-gray-200 p-4 space-y-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">Weather location</div>
          <div className="text-xs text-gray-500 mt-0.5">
            One spot per organization — pick from the areas we currently serve.
          </div>
        </div>

        {cfg.location && (
          <div className="flex items-center gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2" data-testid="weather-current-location">
            <MapPin className="w-4 h-4 text-sky-600 shrink-0" />
            <span className="text-sm font-medium text-sky-900 truncate">{cfg.location.name}</span>
            <span className="ml-auto text-[11px] text-sky-600 shrink-0">
              {Number(cfg.location.lat).toFixed(3)}, {Number(cfg.location.lng).toFixed(3)}
            </span>
          </div>
        )}

        {!cfg.location && cfg.suggestion && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="weather-suggestion-banner">
            <MapPin className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-900 truncate">
              Detected from your services: <span className="font-semibold">{cfg.suggestion.name}</span>
            </span>
            <button
              onClick={() => pickLocation(cfg.suggestion)}
              data-testid="weather-use-suggestion-btn"
              className="ml-auto shrink-0 px-3 py-1 rounded-full text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
            >
              Use this
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {(cfg.presets || []).map((p) => (
            <button
              key={p.name}
              onClick={() => pickLocation(p)}
              data-testid={`weather-preset-${p.name.split(',')[0].toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                isSelected(p)
                  ? 'bg-sky-600 text-white border-sky-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-sky-300 hover:text-sky-700'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Customer report */}
      <div className="rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">Morning briefing to you & your crew</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Daily outlook emailed to the owner and active team members between 7–10am
            </div>
          </div>
          <ServiceToggle
            checked={cfg.teamReport}
            onChange={(v) => save({ teamReport: v })}
            data-testid="weather-team-report-toggle"
          />
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <div>
            <div className="text-sm font-semibold text-gray-900">Morning weather report to customers</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Emailed between 7–10am (your local time) to everyone with a trip that day
            </div>
          </div>
          <ServiceToggle
            checked={cfg.customerReport}
            onChange={(v) => save({ customerReport: v })}
            data-testid="weather-customer-report-toggle"
          />
        </div>
        {cfg.customerReport && (
          <div className="flex items-center justify-between pl-3 border-l-2 border-sky-100">
            <div>
              <div className="text-sm font-medium text-gray-800">Also send as SMS</div>
              <div className="text-xs text-gray-500 mt-0.5">Short text version for customers with a phone number on file</div>
            </div>
            <ServiceToggle
              checked={cfg.customerSms}
              onChange={(v) => save({ customerSms: v })}
              data-testid="weather-customer-sms-toggle"
            />
          </div>
        )}
      </div>

      {/* Quota / refresh */}
      <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 flex items-start justify-between gap-4">
        <p className="text-xs text-gray-500 leading-relaxed">
          We fetch the forecast <span className="font-semibold text-gray-700">once per day</span> for your
          organization — your dashboard, crew and every customer report share that single fetch.
          {fetchedAt && (
            <span className="block mt-1 text-gray-400">
              Last fetched {new Date(fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </p>
        <button
          onClick={refreshNow}
          disabled={refreshing || !cfg.enabled || !cfg.location}
          data-testid="weather-refresh-now-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 bg-white text-gray-700 hover:border-sky-300 hover:text-sky-700 disabled:opacity-50 transition-colors"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh now
        </button>
      </div>
    </div>
  );
};

export default WeatherSettings;
